// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentUserIsGuest,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderStatus/Handlers/Reorder";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ShareEntityButton, { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { CustomOrderModel } from "@west/Overrides/Components/OrderLinesList";
import { EMPTY_VALUE } from "@west/XDL/constants";
import { parseBoolean } from "@west/XDL/Utilities";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
    // XNG-Changes: field to determine whether we're showing title/actions/both
    show = "show",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.generateEmailAttachmentFromWebpage]?: boolean;
        [fields.show]?: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{
        ordernumber?: string;
        stemail?: string;
        stpostalcode?: string;
        orderNumber?: string;
        stEmail?: string;
        stPostalCode?: string;
    }>(location.search);

    return {
        order: state.pages.orderStatus.order,
        isReordering: state.pages.orderStatus.isReordering,
        isUpdatingCart: state.context.isUpdatingCart,
        showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
        stEmail: parsedQuery.stEmail ?? parsedQuery.stemail ?? "",
        stPostalCode: parsedQuery.stPostalCode ?? parsedQuery.stpostalcode ?? "",
        // XNG-Changes:
        rmaLink: getPageLinkByPageType(state, "RequestRmaPage"),
        userIsGuest: getCurrentUserIsGuest(state),
    };
};

const mapDispatchToProps = {
    reorder,
    signInAsGuest,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface OrderStatusHeaderStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    buttonsGridItem?: GridItemProps;
    buttonsHidden?: HiddenProps;
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    printButton?: ButtonPresentationProps;
    buttonsMenuHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    printClickable?: ClickablePresentationProps;
    reorderClickableHidden?: HiddenProps;
    reorderClickable?: ClickablePresentationProps;
    reorderClickableSpinner?: LoadingSpinnerProps;
    reorderButtonHidden?: HiddenProps;
    reorderButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    // XNG-Changes
    printLink?: LinkPresentationProps;
    reorderLink?: LinkPresentationProps;
    seperatorGridItem?: GridItemProps;
    rmaButton?: LinkPresentationProps;
    printLabelLink?: LinkPresentationProps;
}

export const orderStatusHeaderStyles: OrderStatusHeaderStyles = {
    container: {
        gap: 5,
        css: css`
            margin-top: 10px;
        `,
    },
    titleGridItem: { width: [10, 10, 8, 8, 7] },
    titleText: { variant: "h2" },
    buttonsGridItem: {
        width: [2, 2, 4, 4, 5],
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    buttonsHidden: { below: "lg" },
    shareEntityButtonStyles: {
        button: {
            variant: "tertiary",
        },
    },
    printButton: {
        variant: "tertiary",
        css: css`
            margin-left: 10px;
        `,
    },
    buttonsMenuHidden: { above: "md" },
    reorderClickableHidden: { above: "sm" },
    reorderClickableSpinner: {
        size: 22,
        css: css`
            margin: 10px 0 5px 15px;
        `,
    },
    reorderButtonHidden: { below: "md" },
    reorderButton: {
        css: css`
            display: flex;
            align-items: center;
            margin-left: 10px;
        `,
    },
    spinner: {
        size: 22,
        css: css`
            margin: 8px 50px 0 34px;
        `,
    },
};

const styles = orderStatusHeaderStyles;

const OrderStatusHeader = ({
    fields,
    order,
    isReordering,
    isUpdatingCart,
    showAddToCartConfirmationDialog,
    stEmail,
    stPostalCode,
    toaster,
    reorder,
    // XNG-Changes:
    rmaLink,
    userIsGuest,
    signInAsGuest,
}: Props) => {
    const history = useHistory();
    // XNG-Changes: scroll to orderStatus when an order is fetched
    const orderStatusHeaderId = "order-status-header";
    React.useEffect(() => {
        const element = document.getElementById(orderStatusHeaderId);
        if (order && element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [order]);

    if (!order) {
        return null;
    }

    const onClickPrint = () => {
        if (!order) {
            return;
        }
        openPrintDialog();
    };

    const onClickReorder = () => {
        reorder({
            onSuccess: () => {
                if (!showAddToCartConfirmationDialog) {
                    return;
                }
                toaster.addToast({
                    body: `${order.webOrderNumber || order.erpOrderNumber} ${translate("Added to Cart")}`,
                    messageType: "success",
                    timeoutLength: 6000,
                });
            },
        });
    };

    const printLabelUrl = (order as CustomOrderModel).upsReturnLabelLink;

    // XNG-Changes
    const onClickRma = (rmaUrl: string) => {
        if (userIsGuest) {
            // in case user is already logged in as a guest, only redirection to rma is required,
            history.push(
                `${rmaUrl}?orderNumber=${order.webOrderNumber || order.erpOrderNumber}&sTEmail=${encodeURIComponent(
                    stEmail,
                )}`,
            );
        } else {
            // in case user is not signed in as a guest.
            signInAsGuest({
                returnUrl: `${rmaUrl}?orderNumber=${
                    order.webOrderNumber || order.erpOrderNumber
                }&sTEmail=${encodeURIComponent(stEmail)}`,
            });
        }
    };

    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;

    // XNG-Changes
    const { show } = fields;

    // XNG-Changes: We don't show erpOrderNumber in WEST project
    return (
        <GridContainer id={orderStatusHeaderId} {...styles.container}>
            {(show === "title" || show === "both") && (
                <>
                    <GridItem {...styles.seperatorGridItem}></GridItem>
                    <GridItem {...styles.titleGridItem}>
                        <Typography as="h1" {...styles.titleText}>
                            {`${translate("Order #")}${order.webOrderNumber || order.erpOrderNumber}`}
                        </Typography>
                    </GridItem>
                </>
            )}
            {(show === "actions" || show === "both") && (
                <GridItem {...styles.buttonsGridItem}>
                    {/* <Hidden {...styles.buttonsHidden}> */}
                    <ShareEntityButton
                        entityId={order.webOrderNumber}
                        entityName="Order"
                        extendedStyles={styles.shareEntityButtonStyles}
                        generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                        extraProperties={{ stEmail, stPostalCode }}
                    />
                    <Link
                        {...styles.printLink}
                        onClick={onClickPrint}
                        data-test-selector="orderStatusHeader_print"
                        aria-label={translate("Print")}
                    >
                        {translate("Print")}
                    </Link>
                    {/* </Hidden> */}
                    {/* <Hidden {...styles.buttonsMenuHidden}>
                        <OverflowMenu hasChildPortal position="end" {...styles.overflowMenu}>
                            <ShareEntityButton
                                entityId={order.webOrderNumber}
                                entityName="Order"
                                variant="clickable"
                                extendedStyles={styles.shareEntityButtonStyles}
                                extraProperties={{ stEmail, stPostalCode }}
                            />
                            <Clickable {...styles.printClickable} onClick={onClickPrint}>
                                {translate("Print")}
                            </Clickable>
                            <Hidden {...styles.reorderClickableHidden}>
                                {!isReordering && (
                                    <Clickable
                                        {...styles.reorderClickable}
                                        onClick={onClickReorder}
                                        disabled={isUpdatingCart}
                                    >
                                        {translate("Reorder")}
                                    </Clickable>
                                )}
                                {isReordering && <LoadingSpinner {...styles.reorderClickableSpinner} />}
                            </Hidden>
                        </OverflowMenu>
                    </Hidden> */}
                    {/* <Hidden {...styles.reorderButtonHidden}> */}
                    {!isReordering && (
                        <Link
                            {...styles.reorderLink}
                            onClick={onClickReorder}
                            disabled={isUpdatingCart}
                            data-test-selector="orderStatusHeader_reorder"
                            aria-label={translate("Reorder")}
                        >
                            {translate("Reorder")}
                        </Link>
                    )}
                    {isReordering && <LoadingSpinner {...styles.spinner} />}
                    {/* </Hidden> */}
                    {/* XNG-Changes */}
                    {rmaLink && parseBoolean(order.properties?.showRmaButton) && (
                        <Link
                            {...styles.rmaButton}
                            onClick={() => onClickRma(rmaLink.url)}
                            data-test-selector="orderDetails_returnRequestButton"
                            aria-label={translate("Return Request")}
                        >
                            {translate("Return Request")}
                        </Link>
                    )}
                    {printLabelUrl && (
                        <Link
                            {...styles.printLabelLink}
                            href={printLabelUrl}
                            target="_blank"
                            data-test-selector="orderStatusHeader_printLabel"
                            aria-label={translate("Print Label")}
                        >
                            {translate("Print Label")}
                        </Link>
                    )}
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(OrderStatusHeader)),
    definition: {
        group: "Order Status",
        displayName: "Header",
        allowedContexts: ["OrderStatusPage"],
        fieldDefinitions: [
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the order generates an email attachment using the Order Status page, rather than a pre-defined PDF file.",
            },
            // XNG-Changes
            {
                name: "show",
                displayName: "Show",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Title", value: "title" },
                    { displayName: "Actions", value: "actions" },
                    { displayName: "Both", value: "both" },
                ],
                fieldType: "General",
                defaultValue: "title",
            },
        ],
    },
};

export default widgetModule;
